import React, { Component } from "react";
import PropTypes from "prop-types";
import { injectIntl, intlShape } from "react-intl";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Button } from "semantic-ui-react";
import {
  listContexts,
  listDocumentsInContext,
  createDocument
} from "../../actions/fs";
import { selectContext } from "../../actions/tasklist";
import ContextSwitcher from "./ContextSwitcher";
import TaskList from "./TaskList";
import SystemTabs from "./SystemTabs";
import NewDocumentAction from "./NewDocumentAction";
import OpenDocumentAction from "./OpenDocumentAction";

/**
 * Applies the context bar styles.
 */
import "./ContextBar.css";

/**
 * ContextBar : Component
 *
 * (c) Stefan Wittwer 2018 | Alle Rechte vorbehalten.
 *
 * Context bar component which is rendered inside the top bar,
 * containg system tabs, the context switcher and a task list.
 */
class ContextBar extends Component {
  /**
   * state: Object
   * Current component state
   */
  state = {
    /**
     * isNewDocumentSheetOpen: Boolean
     * True if the NewDocumentAction sheet is currently open
     */
    isNewDocumentSheetOpen: false
  };

  /**
   * componentDidMount: ()
   * Fired when the component was mounted, used to dispatch requests
   */
  componentDidMount() {
    this.props.listContexts();
    if (this.props.currentContext !== null) {
      this.props.listDocumentsInContext(this.props.currentContext);
    }
  }

  /**
   * onContextSelected: (event, sender) => ()
   * Fired when a context is selected via the context switcher and
   * updates the current context state.
   */
  onContextSelected = (_, { value }) => {
    this.props.selectContext(value);
    this.props.listDocumentsInContext(value);
  };

  /**
   * setIsNewDocumentSheetOpen: isOpen => ()
   * Sets the "open" state of the new document sheet
   */
  setIsNewDocumentSheetOpen = isOpen => {
    this.setState({
      isNewDocumentSheetOpen: isOpen
    });
  };

  /**
   * createContext: ()
   * Dispatches the creation of a new context
   */
  createContext = () => {
    this.setIsNewDocumentSheetOpen(false);
    this.props.history.push("/create/context");
  };

  /**
   * createDocument: type => ()
   * Dispatches the creation of a new document
   */
  createDocument = type => {
    const document = {
      documentType: type,
      formatVersion: 1,
      filename: this.props.intl.formatMessage({
        id: "files.document.untitled"
      })
    };
    this.setIsNewDocumentSheetOpen(false);

    this.props.createDocument(document, this.props.currentContext);
    this.props.history.push("/document");
  };

  /**
   * render: ()
   * Renders the context bar component.
   */
  render() {
    return (
      <div className="ax-context-bar">
        <SystemTabs
          currentApplication={this.props.location.pathname}
          userType={this.props.userType}
        />
        <ContextSwitcher
          currentContext={this.props.currentContext}
          onContextSelected={this.onContextSelected}
        />
        <Button.Group>
          <NewDocumentAction
            createDocument={this.createDocument}
            createContext={this.createContext}
            onOpenNewSheet={() => this.setIsNewDocumentSheetOpen(true)}
            onCloseNewSheet={() => this.setIsNewDocumentSheetOpen(false)}
            isSheetOpen={this.state.isNewDocumentSheetOpen}
            canCreateDocuments={this.props.currentContext !== null}
          />
          <OpenDocumentAction
            currentContext={this.props.currentContext}
            currentDocument={this.props.currentDocument}
            currentApplication={this.props.location.pathname}
            files={this.props.files}
          />
        </Button.Group>
        <div className="ax-task-list">
          <TaskList
            currentContext={this.props.currentContext}
            currentDocument={this.props.currentDocument}
            currentApplication={this.props.location.pathname}
            files={this.props.files}
          />
        </div>
      </div>
    );
  }
}

ContextBar.propTypes = {
  /**
   * intl: intlShape
   * Localization helper provided by react-intl
   */
  intl: intlShape.isRequired,

  /**
   * location: Object
   * Location object injected by react router, containing the
   * current location path.
   */
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  }).isRequired,

  /**
   * currentContext: String?
   * Identifier of currently selected context
   */
  currentContext: PropTypes.string,

  /**
   * currentDocument: String
   * Document identifier for the currently selected document
   */
  currentDocument: PropTypes.string,

  /**
   * Dispatch actions
   */
  listContexts: PropTypes.func.isRequired,
  listDocumentsInContext: PropTypes.func.isRequired,
  selectContext: PropTypes.func.isRequired,
  createDocument: PropTypes.func.isRequired,

  /**
   * History object provided by withRouter
   */
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,

  /**
   * Type of currenlty logged in user
   */
  userType: PropTypes.string.isRequired,

  /**
   * files: Object
   * Contains lists of currently loaded files ordered by context
   */
  files: PropTypes.shape({}).isRequired
};

ContextBar.defaultProps = {
  currentContext: null,
  currentDocument: null
};

const mapStateToProps = state => ({
  /**
   * Make a deep copy of the state object, instead of
   * shallow, using JSON.parse(JSON.stringify(object))
   */
  files: JSON.parse(JSON.stringify(state.fs.documents || {})),

  currentContext: state.tasklist.currentContext || null,
  currentDocument: state.tasklist.currentDocument || null
});

export default connect(
  mapStateToProps,
  { listContexts, listDocumentsInContext, selectContext, createDocument }
)(injectIntl(withRouter(ContextBar)));



// WEBPACK FOOTER //
// ./src/components/contextbar/ContextBar.js